import React from "react";
import { useI18n } from "../i18n/I18nProvider";
// Removed unnecessary social icons imports (FaFacebookF, etc.)
import { Sparkles } from "lucide-react";
import launchGirl from "../assets/launch.svg";
import { ReactComponent as LogoWhite } from "../assets/logo_white.svg";
// Import icons for professional contact presentation
import { Mail, Globe } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  return (
    <footer className="pt-12 sm:pt-10 pb-6 sm:pb-10 px-4 sm:px-6">
      {/* ================= 1. Launch Section (Styled Background) ================= */}
      <div className="max-w-7xl mx-auto mb-12 sm:mb-16 lg:mb-20">
        {/* Use a clear background and border for professionalism */}
        <div className="bg-[#F9EFD9] rounded-[24px] sm:rounded-[32px] lg:rounded-[40px] p-8 sm:p-12 lg:p-16 xl:p-20 relative overflow-hidden border-2 border-[#F1AF38]/50">
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-16">
            {/* Girl Image - Left Side (Order changed for mobile-friendly flow) */}
            <div className="flex-shrink-0 w-48 h-48 lg:w-56 lg:h-56 xl:w-72 xl:h-72 order-2 md:order-1">
              <img
                src={launchGirl}
                alt="Launch Event Illustration"
                className="w-full h-full object-contain"
              />
            </div>
            {/* Text Content - Right Side */}
            <div className="flex-1 text-center md:text-left relative order-1 md:order-2">
              {/* Sparkle Icons for accent */}
              <div className="flex justify-center md:justify-start gap-1.5 mb-4">
                <Sparkles className="w-8 h-8 text-[#F1AF38] fill-[#F1AF38]" />
              </div>
              {/* Main Heading with professional typography */}
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl xl:text-[42px] 2xl:text-[48px] text-ekatraBlue font-normal leading-snug lg:leading-tight mb-5 sm:mb-6 lg:mb-8"
                style={{ fontFamily: "'Times New Roman', Times, serif" }}
              >
                {t("footer.launch.intro")}{" "}
                <span className="font-bold italic">
                  {t("footer.launch.cityDate")}
                </span>{" "}
                {t("footer.launch.withSpeakers")}{" "}
                <span className="font-bold italic">
                  {t("footer.launch.speakers")}&nbsp;
                </span>
                {t("footer.launch.outro")}
              </h2>
              {/* Sparkle Icons for accent */}
              <div className="flex justify-center md:justify-end gap-1.5 mt-4">
                <Sparkles className="w-10 h-10 text-[#F1AF38] fill-[#F1AF38]" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ================= 2. CTA Section (Clean and Prominent) ================= */}
      <div className="max-w-7xl mx-auto mb-12 sm:mb-16 lg:mb-20">
        <div className="text-center">
          <p
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[36px] text-ekatraBlue font-medium leading-relaxed max-w-4xl mx-auto px-2 sm:px-0"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            {t("footer.cta.prefix")}{" "}
            <a
              href="http://www.ekatrafoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="font-extrabold italic underline decoration-4 underline-offset-8 hover:text-ekatraOrange transition-colors"
            >
              {t("footer.cta.linkText")}
            </a>{" "}
            {t("footer.cta.suffix")}
          </p>
        </div>
      </div>
      {/* ================= 3. Disclaimer Section (Formal) ================= */}
      <div className="max-w-7xl mx-auto mb-12 sm:mb-16 lg:mb-20">
        <div className="bg-[#F9EFD9] rounded-[24px] sm:rounded-[32px] lg:rounded-[40px] p-6 sm:p-10 lg:p-12 text-center border-4 border-ekatraOrange/50 shadow-lg">
          <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-ekatraOrange mb-6 sm:mb-8">
            {t("footer.disclaimer.heading")}
          </h2>
          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-5">
            <p className="text-ekatraBlue text-sm sm:text-base lg:text-lg leading-relaxed">
              {t("footer.disclaimer.statement1")}
            </p>
            <p className="text-ekatraBlue text-sm sm:text-base lg:text-lg leading-relaxed">
              {t("footer.disclaimer.statement2")}
            </p>
            <p className="text-ekatraBlue text-sm sm:text-base lg:text-lg leading-relaxed">
              {t("footer.disclaimer.statement3")}
            </p>
          </div>
        </div>
      </div>
      {/* ================= 4. Footer Panel (Professional & Minimal) ================= */}
      <div className="max-w-7xl mx-auto bg-ekatraBlue text-white rounded-[24px] sm:rounded-[32px] lg:rounded-[40px] p-8 sm:p-10 lg:p-12 xl:p-16 relative">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10 lg:gap-16">
          {/* -------- Column 1: Logo & Brand Name -------- */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <LogoWhite className="w-10 h-10 sm:w-12 sm:h-12" />
            <h3 className="font-lexend font-extrabold text-xl sm:text-2xl lg:text-3xl">
              {t("footer.brand")}
            </h3>
          </div>
          {/* -------- Column 2: Contact Info (Email & Visit) -------- */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 lg:gap-16 font-lexend text-white text-sm sm:text-base lg:text-lg">
            {/* Email Contact */}
          {/*  <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 mt-1 text-ekatraOrange flex-shrink-0" />
              <div className="space-y-1">
                <h4 className="font-bold text-ekatraOrange/90 uppercase text-xs sm:text-sm tracking-widest">
                  {t("footer.contact.title_1")}
                </h4>
                <a
                  href={`mailto:${t("footer.contact.value_1")}`}
                  className="hover:text-ekatraOrange transition-colors text-white"
                >
                  {t("footer.contact.value_1")}
                </a>
              </div>
            </div> */}
            {/* Visit Website - Now a prominent button */}
            <a
              href={t("footer.contact.value_2_link")}
              target="_blank"
              rel="noopener noreferrer"
              // Added button-like styles: padding, background, border, hover effects
              className="group flex items-center justify-center gap-3 bg-ekatraOrange text-ekatraBlue font-bold rounded-lg py-3 px-6 shadow-md transition-all duration-300 hover:bg-ekatraOrange/90 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-ekatraOrange/50 flex-shrink-0 w-full sm:w-auto"
            >
              <Globe className="w-5 h-5 flex-shrink-0 text-ekatraBlue group-hover:scale-110 transition-transform" />
              <span className="uppercase text-sm tracking-wider">
                {t("footer.contact.value_2")}
              </span>
            </a>
          </div>
        </div>
      </div>
      {/* ================= 5. Copyright ================= */}
      <div className="text-center mt-8 sm:mt-10 text-ekatraBlue/70 text-xs sm:text-sm px-4">
        &copy; {new Date().getFullYear()} {t("footer.copyright")}
      </div>
    </footer>
  );
}
